var productName = 'calendar';import DataField from './DataField.js';
import ObjectHelper from '../../helper/ObjectHelper.js';
import StringHelper from '../../helper/StringHelper.js';
/**
 * @module Core/data/field/StoreDataField
 */
/**
 * This field class handles fields that accepts an array that is then converted to a store.
 *
 * ```javascript
 * class Task extends Model {
 *     static get fields() {
 *         return [
 *             'name',
 *             // Store field
 *             { name : 'subTasks', type : 'store', storeClass : Store }
 *         ];
 *     }
 * }
 * ```
 *
 * A record can be constructed like this:
 *
 * ```javascript
 * const task = new Task({
 *     name : 'Task 1',
 *     subTasks : [
 *         { text : 'Something', done : false },
 *         { text : 'Some other thing', done : true }
 *     ]
 * };
 * ```
 *
 * Or by populating a store:
 *
 * ```
 * const store = new Store({
 *     modelClass : Task,
 *     data : [
 *         {
 *             name : 'Task 1',
 *             subTasks : [
 *                 { text : 'Something', done : false },
 *                 { text : 'Some other thing', done : true }
 *             ]
 *         },
 *         ...
 *     ]
 * });
 *
 * ```
 *
 * Whenever the store or its records are manipulated, the field will be marked as modified:
 *
 * ```javascript
 * // These will all be detected as modifications
 * task.subTasks.first.done = true;
 * task.subTasks.last.remove();
 * task.subTasks.add({ text : 'New task', done : false });
 * ```
 *
 * @extends Core/data/field/DataField
 * @classtype store
 * @internal
 */
export default class StoreDataField extends DataField {
    static $name = 'StoreDataField';
    static type = 'store';
    /**
     * Store class to use when creating the store.
     * @config {Class} storeClass
     */
    // Initializer, called when creating a record. Sets up the store and populates it with any initial data
    init(data, record) {
        const
            me        = this,
            storeName = `${me.name}Store`,
            config    = { skipStack : true }; // Optimization when used from sources, don't create a stack in Base
        // Optionally apply modelClass, for convenient configuration
        if (me.modelClass) {
            config.modelClass = me.modelClass;
        }
        // Call optional initializer (initSubTasksStore for subTasks field) on the record, letting it manipulate the
        // config before creating a store
        record[`init${StringHelper.capitalize(storeName)}`]?.(config);
        if (!config.storeClass && !me.storeClass) {
            throw new Error(`Field '${me.name}' with type 'store' must have a storeClass configured`);
        }
        // Store has to be assigned on the record, field is shared
        const store = record.meta[storeName] = new (config.storeClass || me.storeClass)(config);
        if (me.complexMapping) {
            ObjectHelper.setPath(data, me.dataSource, store);
        }
        else {
            data[me.dataSource] = store;
        }
        // Don't warn about generated ids, responsibility lies elsewhere
        store.verifyNoGeneratedIds = false;
        // Keep track of if id should be included when serializing or not
        store.usesId = !store.count || !store.every(record => record.hasGeneratedId);
        // Cache value
        store.$currentValue = me.getValue(store);
        // Track changes to the store, applying them to the record and caching current value to be used when
        // serializing and in comparisons (required, otherwise we would be comparing to already updated store
        store.on('change', () => {
            const value = me.getValue(store);
            if (!store.$isSettingStoreFieldData) {
                record.set(me.name, value);
            }
            store.$currentValue = value;
        });
    }
    // Called when setting a new value to the field on a record
    set(value, data, record) {
        const
            me        = this,
            storeName = `${me.name}Store`,
            { [storeName] : store } = record.meta;
        // Lazy store might not be created yet, gets created on first access. Returning false keeps the value for later
        // if called during init
        if (!store) {
            return false;
        }
        // Prevent changes from leading to recursive calls
        if (store.$isSettingStoreFieldData) {
            return;
        }
        store.$isSettingStoreFieldData = true;
        // Call optional processing fn (processSubTasksStoreData for subTasks field) on the record, letting it
        // manipulate the data before creating records
        value = record[`process${StringHelper.capitalize(storeName)}Data`]?.(value, record) ?? value;
        // Apply incoming array to store
        store.data = value;
        store.$isSettingStoreFieldData = false;
        // Keep track of if id should be included when serializing or not
        store.usesId = !store.count || !store.every(record => record.hasGeneratedId);
    }
    serialize(value, record) {
        return record.meta[`${this.name}Store`].$currentValue;
    }
    // Extract persistable values, optionally including id depending on if ids are used
    getValue(store) {
        return store.allRecords.map(r => {
            const data = r.persistableData;
            if (!store.usesId) {
                delete data.id;
            }
            return data;
        });
    }
    isEqual(a, b) {
        if (a?.isStore) {
            a = a.$currentValue;
        }
        if (b?.isStore) {
            b = b.$currentValue;
        }
        return ObjectHelper.isDeeplyEqual(a, b);
    }
}
StoreDataField.initClass();
StoreDataField._$name = 'StoreDataField';